import { useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  Edit,
  ZoomIn,
} from '@mui/icons-material';
import Stack from '@/components/Stack';
import { Body, Small, Caption } from '@/components/Typography';
import Badge from '@/components/Badge';
import { ConsolidatedCertificate } from '@/services/workersV2API';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  certificate: ConsolidatedCertificate;
  index: number;
  totalCount: number;
  onUpdate: () => void;
  onError: (title: string, message: string) => void;
};

const STATUS_CONFIG = {
  verified: {
    icon: CheckCircle,
    color: '#10B981',
    bgColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    label: 'Verified',
    badgeStatus: 'theme' as const,
  },
  rejected: {
    icon: Cancel,
    color: '#EF4444',
    bgColor: '#FEF2F2',
    borderColor: '#FECACA',
    label: 'Rejected',
    badgeStatus: 'danger' as const,
  },
  pending: {
    icon: Schedule,
    color: '#F59E0B',
    bgColor: '#FFFBEB',
    borderColor: '#FDE68A',
    label: 'Pending Review',
    badgeStatus: 'warning' as const,
  },
};

const CertificateReviewSection = ({
  certificate,
  index,
  totalCount,
  onUpdate,
  onError,
}: Props) => {
  const [processing, setProcessing] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [expiryDate, setExpiryDate] = useState(
    certificate.expires_at
      ? new Date(certificate.expires_at).toISOString().split('T')[0]
      : '',
  );
  const [showImageModal, setShowImageModal] = useState(false);

  const statusConfig =
    STATUS_CONFIG[certificate.status as keyof typeof STATUS_CONFIG] ||
    STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  const isExpired =
    certificate.expires_at && new Date(certificate.expires_at) < new Date();
  const isExpiringSoon =
    certificate.expires_at &&
    !isExpired &&
    new Date(certificate.expires_at) <
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const handleVerify = async () => {
    setProcessing(true);
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/certificate_photos/${certificate.id}/verify`,
      );
      if (response.data.success) {
        onUpdate();
      } else {
        onError('Error', 'Failed to verify certificate');
      }
    } catch (error) {
      console.error('Error verifying certificate:', error);
      onError('Error', 'Failed to verify certificate');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    setShowRejectDialog(false);
    try {
      const response = await axiosRubyClient.post(
        `/api/admin/certificate_photos/${certificate.id}/reject`,
        { comment: rejectReason || undefined },
      );
      if (response.data.success) {
        setRejectReason('');
        onUpdate();
      } else {
        onError('Error', 'Failed to reject certificate');
      }
    } catch (error) {
      console.error('Error rejecting certificate:', error);
      onError('Error', 'Failed to reject certificate');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveExpiry = async () => {
    setProcessing(true);
    try {
      const response = await axiosRubyClient.patch(
        `/api/admin/certificate_photos/${certificate.id}`,
        { expires_at: expiryDate || null },
      );
      if (response.data.success) {
        setShowExpiryDialog(false);
        onUpdate();
      } else {
        onError('Error', 'Failed to update expiry date');
      }
    } catch (error) {
      console.error('Error updating expiry:', error);
      onError('Error', 'Failed to update expiry date');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <>
      <div
        style={{
          backgroundColor: statusConfig.bgColor,
          border: `1px solid ${statusConfig.borderColor}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: index < totalCount - 1 ? '16px' : 0,
        }}
      >
        {/* Header */}
        <Stack
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          <Stack gap={8} style={{ alignItems: 'center' }}>
            <StatusIcon
              style={{ color: statusConfig.color, fontSize: '20px' }}
            />
            <Body weight="semibold">
              {certificate.type
                ? certificate.type
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())
                : `Certificate ${index + 1}`}
            </Body>
            {index === 0 && <Badge label="Latest" status="neutral" />}
          </Stack>
          <Badge label={statusConfig.label} status={statusConfig.badgeStatus} />
        </Stack>

        {/* Certificate Image */}
        <div
          style={{
            position: 'relative',
            marginBottom: '12px',
            cursor: 'pointer',
          }}
          onClick={() => setShowImageModal(true)}
        >
          <img
            src={certificate.image_url || ''}
            alt="Certificate"
            style={{
              width: '100%',
              maxHeight: '300px',
              objectFit: 'contain',
              borderRadius: '8px',
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              backgroundColor: 'rgba(0,0,0,0.6)',
              borderRadius: '4px',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <ZoomIn style={{ color: '#fff', fontSize: '16px' }} />
            <Caption style={{ color: '#fff' }}>Click to enlarge</Caption>
          </div>
        </div>

        {/* Metadata */}
        <Stack vertical gap={8} style={{ marginBottom: '16px' }}>
          <Stack style={{ justifyContent: 'space-between' }}>
            <Small color="lighter">Uploaded</Small>
            <Small>{formatDateTime(certificate.created_at)}</Small>
          </Stack>

          <Stack
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Small color="lighter">Expiry Date</Small>
            <Stack gap={8} style={{ alignItems: 'center' }}>
              {certificate.expires_at ? (
                <>
                  <Small
                    color={
                      isExpired
                        ? 'danger'
                        : isExpiringSoon
                          ? 'warning'
                          : undefined
                    }
                    weight={
                      isExpired || isExpiringSoon ? 'semibold' : undefined
                    }
                  >
                    {formatDate(certificate.expires_at)}
                    {isExpired && ' (Expired)'}
                    {isExpiringSoon && !isExpired && ' (Expiring Soon)'}
                  </Small>
                  {(isExpired || isExpiringSoon) && (
                    <Warning
                      style={{
                        color: isExpired ? '#EF4444' : '#F59E0B',
                        fontSize: '16px',
                      }}
                    />
                  )}
                </>
              ) : (
                <Small color="lighter">Not set</Small>
              )}
              <Tooltip title="Edit expiry date">
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowExpiryDialog(true)}
                  style={{ minWidth: 'auto', padding: '2px' }}
                >
                  <Edit style={{ fontSize: '16px' }} />
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>

        {/* Action Buttons */}
        {certificate.status !== 'verified' &&
          certificate.status !== 'rejected' && (
            <Stack gap={12}>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={
                  processing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircle />
                  )
                }
                onClick={handleVerify}
                disabled={processing}
                style={{ flex: 1 }}
              >
                {processing ? 'Verifying...' : 'Verify Certificate'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Cancel />}
                onClick={() => setShowRejectDialog(true)}
                disabled={processing}
                style={{ flex: 1 }}
              >
                Reject
              </Button>
            </Stack>
          )}

        {/* Already reviewed message */}
        {certificate.status === 'verified' && (
          <Stack
            gap={8}
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              backgroundColor: '#fff',
              borderRadius: '6px',
            }}
          >
            <CheckCircle style={{ color: '#10B981', fontSize: '18px' }} />
            <Small color="lighter">Certificate has been verified</Small>
          </Stack>
        )}

        {certificate.status === 'rejected' && (
          <Stack vertical gap={8}>
            <Stack
              gap={8}
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
                backgroundColor: '#fff',
                borderRadius: '6px',
              }}
            >
              <Cancel style={{ color: '#EF4444', fontSize: '18px' }} />
              <Small color="lighter">Certificate has been rejected</Small>
            </Stack>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={
                processing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircle />
                )
              }
              onClick={handleVerify}
              disabled={processing}
              fullWidth
            >
              {processing ? 'Verifying...' : 'Approve Instead'}
            </Button>
          </Stack>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Certificate</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Please provide a reason for rejecting this certificate (optional).
            This will be visible to the worker.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g., Certificate is expired, image is blurry..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={processing}
          >
            {processing ? 'Rejecting...' : 'Reject Certificate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expiry Dialog */}
      <Dialog
        open={showExpiryDialog}
        onClose={() => setShowExpiryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expiry Date</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Set the expiration date for this certificate.
          </DialogContentText>
          <TextField
            type="date"
            fullWidth
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            label="Expiry Date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExpiryDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveExpiry}
            variant="contained"
            color="primary"
            disabled={processing}
          >
            {processing ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent style={{ padding: 0 }}>
          <img
            src={certificate.image_url || ''}
            alt="Certificate"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImageModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificateReviewSection;
